// Copy for the inventory boards (board 14 part 2 §ز: inventory/items,
// item--edit, purchase--draft, suppliers, count--in-progress, moves--item;
// HANDOFF §6/§7) and the Arabic count agreement the boards use. Every figure
// passed in is an API value — these helpers only phrase it (rule 7).
// Terminology (المراجع/الزبون) is NEVER in this file: callers pass t('customer').

import { arNumber, formatMoney, formatRate } from '@/lib/format';
import type { PillTone } from '@/components';
import type { CountStatus, MoveDocumentType, PurchaseStatus, StockStatus } from '../types';

export const COPY = {
  module: 'المخزون',
  menu: {
    items: 'المواد',
    purchases: 'قوائم الشراء',
    suppliers: 'الموردون',
    counts: 'الجرد',
    moves: 'الحركات',
  },
  print: 'طباعة',
  export: 'تصدير',
  refresh: 'حدّث الشاشة',
  cancel: 'إلغاء',
  /** The «keep it» exit of a cancel confirm (never «إلغاء» next to «إلغاء الجرد»). */
  back: 'تراجع',
  close: 'إغلاق',
  actions: 'إجراءات',
  dash: '—',
  exportFailed: 'لم تنجح المحاولة.',
  shownOf: (shown: number, total: number) => `تُعرض ${arNumber(shown)} من ${arNumber(total)}`,
  /** The register pager (the settings / import «السابق · التالي» pattern). */
  pager: {
    label: 'الصفحات',
    prev: 'السابق',
    next: 'التالي',
    range: (from: number, to: number, total: number) => `${arNumber(from)}–${arNumber(to)} من ${arNumber(total)}`,
  },
  /** Server-side search above a picker whose list is longer than one answer (proposed copy — OPEN-QUESTIONS). */
  picker: {
    searchItems: 'ابحث عن مادة',
    searchSuppliers: 'ابحث عن مورد',
  },

  items: {
    title: 'المواد',
    eyebrow: (branch?: string | null) => ['المخزون', 'المواد', branch].filter(Boolean).join(' · '),
    all: 'الكل',
    belowMin: 'تحت الحدّ',
    newItem: 'مادة جديدة',
    kpiValue: 'قيمة المخزون بالتكلفة',
    kpiBelow: 'تحت الحدّ',
    kpiItemUnit: 'مادة',
    /** «تنتهي خلال ٣٠ يوماً» — the window is the API's `kpi.expiring_window_days`. */
    kpiExpiring: (days: number) => `تنتهي خلال ${daysText(days)}`,
    kpiIssues: (month: string) => `استهلاك ${month}`,
    kpiIssuesUnit: 'حركة صرف',
    cols: {
      item: 'المادة',
      category: 'الفئة',
      unit: 'الوحدة',
      onHand: 'الرصيد',
      min: 'الحدّ الأدنى',
      cost: 'التكلفة',
      price: 'سعر البيع',
      status: 'الحالة',
    },
    /** Pack sub-line from the chain's top step: «٥ عبوة في الكرتون». */
    pack: (factor: number, next: string, top: string) => `${arNumber(factor)} ${next} في ${definite(top)}`,
    /** «صلاحية ١٠ تشرين الأول ٢٠٢٦» (the nearest expiry of the stock on hand). */
    expiry: (date: string) => `صلاحية ${date}`,
    lastPurchase: (date: string) => `آخر شراء ${date}`,
    supplier: (name: string) => `مورد: ${name}`,
    /** «للبيع للمراجع» — `customer` is t('customer'). */
    forSale: (customer: string) => `للبيع ${lamOf(customer)}`,
    emptyFirst: 'لا مواد بعد.',
    menuOpen: 'فتح',
    menuMoves: 'حركة المادة',
    menuStop: 'إيقاف المادة',
  },

  item: {
    panelTitle: 'بطاقة المادة',
    docPill: 'مادة ·',
    name: 'اسم المادة',
    category: 'الفئة',
    barcode: 'الباركود',
    unit: 'الوحدة',
    units: 'الوحدات',
    /**
     * The chain helper (board: «الشراء بالكرتون، الصرف بالعبوة، العدّ بالقطعة»),
     * phrased from the item's own chain and what erp-core does with it: a
     * purchase line is entered in any unit (the largest by default); issues
     * and counts are in the base unit.
     */
    unitsHelper: (top: string, base: string) => `الشراء ${bi(top)}، الصرف والعدّ ${bi(base)}`,
    /** PROPOSED (HANDOFF س٨): the chain editor's row controls. */
    unitName: 'اسم الوحدة',
    baseUnitName: 'الوحدة الأساس',
    unitFactor: (label: string) => `عدد الوحدات الأصغر في ${definite(label || 'الوحدة')}`,
    addUnit: 'إضافة وحدة أكبر',
    removeUnit: 'حذف الوحدة',
    unitsInvalid: 'أكمل اسم كل وحدة، والعدد في الوحدة الأكبر ٢ أو أكثر.',
    minUnit: 'وحدة الحدّ الأدنى',
    expiry: 'تنبيه الصلاحية',
    expiryUnit: 'يوماً',
    /** PROPOSED: empty = the expiry is not tracked. */
    expiryHint: 'فارغ = لا تُتابَع الصلاحية',
    min: 'الحدّ الأدنى',
    cost: 'التكلفة (متوسط مرجّح)',
    costHint: 'يُحسب من قوائم الشراء · لا يُحرَّر',
    price: 'سعر البيع',
    /** «فارغ = لا تُباع للمراجع» — `customer` is t('customer'). */
    priceHint: (customer: string) => `فارغ = لا تُباع ${lamOf(customer)}`,
    balances: 'الأرصدة بالمخازن',
    notTracked: '— لا يُتابَع هنا',
    save: 'حفظ',
    stop: 'إيقاف المادة',
    saved: 'حُفظت المادة.',
    stopped: 'أُوقفت المادة.',
    required: 'اسم المادة مطلوب.',
    invalidNumber: 'أدخل رقماً صحيحاً لا يقل عن صفر.',
    stopTitle: 'إيقاف المادة؟',
    stopBody: 'تختفي المادة من القوائم ويبقى تاريخ حركتها. لا تُحذف مادة لها حركة.',
    stopConfirm: 'إيقاف المادة',
    dirtyTitle: 'لم تُحفظ التعديلات.',
    dirtyBody: 'إن أغلقت البطاقة الآن تضيع التعديلات.',
    dirtyDiscard: 'تجاهل التعديلات',
    dirtyKeep: 'متابعة التحرير',
  },

  purchases: {
    title: 'قوائم الشراء',
    eyebrow: 'المخزون · قوائم الشراء',
    newPurchase: 'قائمة شراء جديدة',
    chips: { all: 'الكل', draft: 'المسودات', posted: 'المرحَّلة' } as Record<'all' | 'draft' | 'posted', string>,
    cols: { no: 'الرقم', supplier: 'المورد', date: 'التاريخ', status: 'الحالة', lines: 'الأسطر', total: 'المجموع' },
    emptyFirst: 'لا قوائم شراء بعد.',
  },

  purchase: {
    title: 'قائمة شراء',
    eyebrow: 'المخزون · قوائم الشراء',
    saveDraft: 'حفظ مسودة',
    receive: 'استلام وترحيل',
    reverse: 'قيد عكس بتاريخ اليوم',
    supplier: 'المورد',
    supplierPick: 'اختر المورد',
    balanceBefore: (amount: string) => `الرصيد الدائن قبل هذه القائمة: ${amount}`,
    invoiceNo: 'رقم فاتورة المورد',
    payment: 'الدفع',
    credit: (days: number) => `آجل · ${daysText(days)}`,
    cash: 'نقد',
    /** Board purchase--draft caption: «النقد يصدر سند صرف من الصندوق المختار». */
    cashHint: 'النقد يصدر سند صرف من الصندوق المختار',
    till: 'الصندوق',
    tillPick: 'اختر الصندوق',
    /** The server's own sentence (422 till_id/required). */
    needTill: 'اختر الصندوق الذي يُدفع منه',
    voucher: 'سند صرف',
    voucherAwaiting: 'بانتظار تأكيد المالك',
    cancelDraft: 'إلغاء المسودة',
    cancelTitle: 'إلغاء المسودة؟',
    cancelBody: 'تُلغى قائمة الشراء: لا يُستلم منها شيء ولا يُقيَّد قيد، ويبقى رقمها في السجل.',
    cancelled: 'أُلغيت المسودة.',
    cols: { item: 'المادة', qty: 'الكمية', unit: 'الوحدة', price: 'سعر الوحدة', total: 'المجموع' },
    itemPick: 'اختر المادة',
    addLine: 'إضافة مادة',
    removeLine: 'حذف السطر',
    total: 'المجموع',
    discount: 'خصم المورد',
    net: 'الصافي',
    usd: 'بالدولار',
    usdValue: (usd: string, rate: string) => `≈ ${usd} $ بسعر ${rate}`,
    rate: (rate: string) => `د.ع · سعر ${rate}`,
    saved: 'حُفظت المسودة.',
    posted: (entry: string | null) => (entry ? `رُحّلت قائمة الشراء · القيد ${entry}` : 'رُحّلت قائمة الشراء.'),
    reversed: 'عُكست قائمة الشراء بقيد بتاريخ اليوم.',
    reverseTitle: 'عكس قائمة الشراء؟',
    reverseBody: 'تخرج الكميات المستلمة بتكلفتها ويُقيَّد قيد عكس بتاريخ اليوم. لا يُعدَّل المستند المرحَّل.',
    lineInvalid: 'أكمل المادة والكمية والسعر في كل سطر.',
    needSupplier: 'اختر المورد أولاً.',
    needLines: 'أضف مادة واحدة على الأقل.',
    lockedTitle: 'فترة مقفلة',
    lockedDateToday: 'ترحيل بتاريخ اليوم',
    lockedReopen: 'طلب إعادة الفتح',
  },

  suppliers: {
    title: 'الموردون',
    eyebrow: 'المخزون · الموردون',
    newSupplier: 'مورد جديد',
    kpiPayable: 'الرصيد الدائن',
    kpiDue30: 'يستحق خلال ٣٠ يوماً',
    kpiPurchases: (month: string) => `مشتريات ${month}`,
    cols: { supplier: 'المورد', phone: 'الهاتف', last: 'آخر شراء', open: 'قوائم مفتوحة', balance: 'الرصيد الدائن' },
    statement: 'كشف حساب',
    payVoucher: 'سند صرف',
    emptyFirst: 'لا موردين بعد.',
    panelNew: 'مورد جديد',
    panelEdit: 'بيانات المورد',
    name: 'اسم المورد',
    phone: 'الهاتف',
    category: 'الفئة',
    city: 'المدينة',
    note: 'ملاحظة',
    save: 'حفظ',
    saved: 'حُفظ المورد.',
    required: 'اسم المورد مطلوب.',
  },

  counts: {
    title: 'الجرد',
    eyebrow: 'المخزون · الجرد',
    newCount: 'جرد جديد',
    cols: { no: 'الرقم', warehouse: 'المخزن', status: 'الحالة', progress: 'العدّ', diffValue: 'قيمة الفرق', started: 'بدأ' },
    emptyFirst: 'لا جرد بعد.',
    panelTitle: 'جرد جديد',
    warehouse: 'المخزن',
    start: 'ابدأ الجرد',
  },

  count: {
    title: 'جرد',
    eyebrow: (warehouse: string) => `المخزون · الجرد · ${warehouse}`,
    saveLater: 'حفظ والمتابعة لاحقاً',
    approve: 'اعتماد الجرد',
    reverse: 'قيد عكس بتاريخ اليوم',
    info: 'الفرق يُرحَّل قيد تسوية مخزون عند الاعتماد؛ لا يُعتمد والعدّ ناقص.',
    cols: { item: 'المادة', unit: 'الوحدة', book: 'رصيد الدفتر', counted: 'العدّ الفعلي', diff: 'الفرق', diffValue: 'قيمة الفرق' },
    footer: 'الفرق حتى الآن',
    countedLabel: (name: string) => `العدّ الفعلي · ${name}`,
    approved: (entry: string | null) => (entry ? `اعتُمد الجرد · القيد ${entry}` : 'اعتُمد الجرد.'),
    reversed: 'عُكس الجرد بقيد بتاريخ اليوم.',
    reverseTitle: 'عكس الجرد؟',
    reverseBody: 'تُعكس تسويات الجرد ويُقيَّد قيد عكس بتاريخ اليوم.',
    cancel: 'إلغاء الجرد',
    cancelTitle: 'إلغاء الجرد؟',
    cancelBody: 'يُلغى الجرد ولا تُرحَّل فروقه، ويمكن بعدها بدء جرد جديد لهذا المخزن.',
    cancelled: 'أُلغي الجرد.',
    invalid: 'أدخل عدداً صحيحاً لا يقل عن صفر.',
  },

  moves: {
    title: 'الحركات',
    titleItem: (name: string) => `حركة المادة · ${name}`,
    eyebrow: 'المخزون · الحركات',
    allKinds: 'كل الحركات',
    allItems: 'كل المواد',
    allPeriods: 'كل الفترات',
    issue: 'سند صرف مخزني',
    kpiOnHand: 'الرصيد الآن',
    kpiConsumed: (month: string) => `استُهلك في ${month}`,
    kpiInvoiced: 'صُرف على فواتير',
    kpiAvgCost: 'متوسط التكلفة',
    avgCostUnit: (unit: string) => `د.ع / ${unit}`,
    cols: { time: 'الوقت', kind: 'الحركة', doc: 'المستند', qty: 'الكمية', after: 'الرصيد بعدها', warehouse: 'المخزن' },
    emptyFirst: 'لا حركات بعد.',
  },

  issue: {
    panelTitle: 'سند صرف مخزني',
    item: 'المادة',
    itemPick: 'اختر المادة',
    qty: 'الكمية',
    warehouse: 'المخزن',
    note: 'ملاحظة',
    save: 'حفظ وترحيل',
    saved: (no: string) => `رُحّل سند الصرف المخزني ${no}`,
    qtyInvalid: 'أدخل كمية أكبر من صفر.',
  },

  pill: {
    stock: {
      ok: { label: 'متوفر', tone: 'ok' },
      below_min: { label: 'تحت الحدّ', tone: 'warn' },
      out: { label: 'نفد', tone: 'err' },
      expiring: { label: 'تنتهي خلال ٣٠ يوماً', tone: 'warn' },
    } as Record<StockStatus, { label: string; tone: PillTone }>,
    purchase: {
      draft: { label: 'مسودة', tone: 'neutral' },
      posted: { label: 'مرحَّلة', tone: 'ok' },
      cancelled: { label: 'ملغاة', tone: 'neutral' },
      reversed: { label: 'معكوسة', tone: 'neutral' },
    } as Record<PurchaseStatus, { label: string; tone: PillTone }>,
    count: {
      in_progress: { label: 'جارٍ', tone: 'ok' },
      finished: { label: 'معتمد', tone: 'ok' },
      cancelled: { label: 'ملغى', tone: 'neutral' },
      reversed: { label: 'معكوس', tone: 'neutral' },
    } as Record<CountStatus, { label: string; tone: PillTone }>,
  },

  /** The document word before a move's LTR number (board moves--item). */
  docWord: {
    invoice: 'فاتورة',
    purchase: 'قائمة شراء',
    stock_issue: 'سند صرف مخزني',
    stock_count: 'جرد',
    stock_transfer: 'تحويل',
    item_opening: 'رصيد افتتاحي',
  } as Record<MoveDocumentType, string>,
} as const;

/** A unit label with the definite article: «كرتون» → «الكرتون» (already definite stays). */
export function definite(word: string): string {
  return word.startsWith('ال') ? word : `ال${word}`;
}

/** «ب» + the definite label: «بالكرتون». */
function bi(word: string): string {
  return `ب${definite(word)}`;
}

/**
 * The item status pill. «تنتهي خلال …» reads the item's own `expires_in_days`
 * when the API sent it (the board's «٣٠ يوماً» is the sample); an unknown
 * status from a newer server renders nothing rather than crashing (rule 6).
 */
export function stockPill(status: string, expiresInDays?: number | null): { label: string; tone: PillTone } | null {
  const p = (COPY.pill.stock as Record<string, { label: string; tone: PillTone } | undefined>)[status];
  if (!p) return null;
  if (status !== 'expiring' || expiresInDays == null) return p;
  if (expiresInDays < 0) return { label: 'انتهت الصلاحية', tone: 'err' };
  if (expiresInDays === 0) return { label: 'تنتهي اليوم', tone: p.tone };
  return { label: `تنتهي خلال ${daysText(expiresInDays)}`, tone: p.tone };
}

/** «المراجع» → «للمراجع», «الزبون» → «للزبون» (ل + ال ⇒ لل). */
export function lamOf(word: string): string {
  return word.startsWith('ال') ? `لل${word.slice(2)}` : `ل${word}`;
}

/** «مادة» agreement: ١ مادة واحدة · مادتان · ٣–١٠ مواد · ١١+ مادة. */
export function itemsCount(n: number): string {
  if (n === 1) return 'مادة واحدة';
  if (n === 2) return 'مادتان';
  if (n >= 3 && n <= 10) return `${arNumber(n)} مواد`;
  return `${arNumber(n)} مادة`;
}

/** Items head pill (board: «٧ مواد · ٢ تحتاج شراء»). */
export function itemsPill(count: number, needsPurchase: number): { label: string; tone: PillTone } {
  if (needsPurchase > 0) return { label: `${itemsCount(count)} · ${arNumber(needsPurchase)} تحتاج شراء`, tone: 'warn' };
  return { label: itemsCount(count), tone: 'neutral' };
}

/** «لمورد واحد» · «لموردَين» · «لـ٣ موردين» · «لـ١١ مورداً». */
export function suppliersFor(n: number): string {
  if (n === 1) return 'لمورد واحد';
  if (n === 2) return 'لموردَين';
  if (n >= 3 && n <= 10) return `لـ${arNumber(n)} موردين`;
  return `لـ${arNumber(n)} مورداً`;
}

/** Suppliers head pill (board: «علينا ٢٠٥٬٠٠٠ د.ع لموردَين»). */
export function suppliersPill(totalMinor: number, withBalance: number, currency = 'IQD'): string {
  return `علينا ${formatMoney(totalMinor, currency)} ${suppliersFor(withBalance)}`;
}

/** «يوم واحد» · «يومان» · «٣ أيام» · «٣٠ يوماً». */
export function daysText(n: number): string {
  if (n === 1) return 'يوم واحد';
  if (n === 2) return 'يومان';
  if (n >= 3 && n <= 10) return `${arNumber(n)} أيام`;
  return `${arNumber(n)} يوماً`;
}

/** Count progress pill: «جارٍ · ٤ من ٧». */
export function countPill(status: CountStatus, counted: number, total: number): { label: string; tone: PillTone } {
  const p = COPY.pill.count[status];
  if (status === 'in_progress') return { label: `${p.label} · ${arNumber(counted)} من ${arNumber(total)}`, tone: p.tone };
  return p;
}

/** A numeric-string rate from the server («1320.0000») → «١٬٣٢٠». Display only. */
export function rateText(rate: string | null | undefined): string | null {
  if (!rate) return null;
  const n = Number(rate);
  return Number.isFinite(n) && n > 0 ? formatRate(n) : null;
}

/** «166.67» (server string) → «١٦٦٫٦٧». Display only. */
export function usdText(v: string | null | undefined): string | null {
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? arNumber(n, { decimals: 2 }) : null;
}
